import React from 'react';
import { IRecommendationSales } from '../../interfaces/recommendationSales';
import { toFixedThree } from '../../utility/helper';

interface RecommendationSalesReportProps {
    data?: IRecommendationSales;
    loading: boolean;
    error: boolean;
}

const RecommendationSalesReport: React.FC<RecommendationSalesReportProps> = ({ data, loading, error }) => {
    const metrics = data ? [
        { label: 'Units sold', value: data.totalUnitsSold.toLocaleString() },
        { label: 'Revenue', value: `BHD ${toFixedThree(data.totalRevenue)}` },
        { label: 'Recommendation orders', value: data.recommendationOrders.toLocaleString() },
        { label: 'Conversion rate', value: `${data.conversionRate.toFixed(2)}%` },
    ] : [];

    return (
        <section className="mt-4 mb-4" aria-labelledby="recommendation-sales-title">
            <h2 id="recommendation-sales-title" className="app-page-title mb-1">Suggested product performance</h2>
            <p className="text-muted mb-4">Results for the selected date range</p>

            {loading || error || !data ? (
                <div className="app-card app-card-orders-table shadow-sm p-4" role="status">
                    {loading
                        ? 'Loading suggested product sales...'
                        : error
                            ? 'Unable to load suggested product sales.'
                            : 'Suggested product sales data is not available for this period.'}
                </div>
            ) : (
                <>
                    <div className="row g-3 mb-4">
                        {metrics.map((metric) => (
                            <div className="col-12 col-sm-6 col-xl-3" key={metric.label}>
                                <div className="app-card app-card-orders-table shadow-sm p-4 h-100">
                                    <div className="text-muted small fw-semibold mb-2">{metric.label}</div>
                                    <div className="fs-4 fw-bold text-primary">{metric.value}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="app-card app-card-orders-table shadow-sm p-4">
                        <h3 className="card-title mb-3">Suggested products sold</h3>
                        <div className="table-responsive">
                            <table className="table app-table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">Product</th>
                                        <th scope="col" className="text-end">Times sold</th>
                                        <th scope="col" className="text-end">Units sold</th>
                                        <th scope="col" className="text-end">Revenue (BHD)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.products?.length ? data.products.map((product) => (
                                        <tr key={product.productId}>
                                            <td className="text-break fw-semibold">{product.productName}</td>
                                            <td className="text-end">{product.timesSold.toLocaleString()}</td>
                                            <td className="text-end">{product.totalQuantity.toLocaleString()}</td>
                                            <td className="text-end">{toFixedThree(product.totalRevenue)}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={4} className="text-center text-muted py-4">
                                                No suggested products were sold in this period.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

export default RecommendationSalesReport;
